import React, { useEffect, useState, useCallback } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { api, formatCurrency } from "@/lib/api";
import { logger } from "@/lib/logger";
import { toast } from "sonner";
import { AlertCircle, Home, X, Sparkles } from "lucide-react";

/**
 * Reviews high-value expense transactions from the last 90 days that are
 * NOT yet linked to any real-estate property. Lets the user quickly tag
 * them or dismiss them (skip = mark as reviewed via a client-side hint).
 */
export default function HighValueReviewDialog({ open, onOpenChange, threshold = 2000 }) {
  const [items, setItems] = useState([]);
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState(null);
  // Client-side "hide" for items the user skipped without linking
  const [hidden, setHidden] = useState(() => new Set());
  // AI suggestions map: {tx_id: {property_id, confidence, reason}}
  const [suggestions, setSuggestions] = useState({});
  const [suggesting, setSuggesting] = useState(false);
  const [autolinkThreshold, setAutolinkThreshold] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [hv, inv, prefs] = await Promise.all([
        api.get("/transactions/high-value-unlinked", { params: { threshold } }),
        api.get("/investments"),
        api.get("/notifications/prefs").catch(() => ({ data: {} })),
      ]);
      setItems(hv.data?.items || []);
      setProperties((inv.data || []).filter((i) => i.type === "real_estate"));
      const t = prefs.data?.ai_autolink_threshold;
      setAutolinkThreshold(t === null || t === undefined ? null : Number(t));
    } catch (e) {
      logger.warn("high-value review", e);
    } finally {
      setLoading(false);
    }
  }, [threshold]);

  useEffect(() => { if (open) load(); }, [open, load]);

  const link = async (tx, propertyId) => {
    setSavingId(tx.id);
    try {
      await api.patch(`/transactions/${tx.id}`, { linked_property_id: propertyId });
      toast.success("Vinculado a inmueble");
      setItems((prev) => prev.filter((t) => t.id !== tx.id));
    } catch {
      toast.error("No se pudo vincular");
    } finally {
      setSavingId(null);
    }
  };

  const skip = async (tx) => {
    // Optimistically hide, then persist the review so it doesn't come back
    // on the next dashboard load.
    setHidden((prev) => new Set(prev).add(tx.id));
    try {
      await api.patch(`/transactions/${tx.id}`, { hv_reviewed: true });
    } catch (e) {
      logger.warn("hv skip persist failed", e);
      // Roll back the optimistic hide so the user can try again.
      setHidden((prev) => {
        const next = new Set(prev);
        next.delete(tx.id);
        return next;
      });
      toast.error("No se pudo marcar como revisado");
    }
  };

  const suggestAll = async () => {
    if (items.length === 0) return;
    setSuggesting(true);
    try {
      const r = await api.post("/transactions/suggest-property", {
        tx_ids: items.map((t) => t.id),
      });
      const sug = r.data?.suggestions || {};
      setSuggestions(sug);
      const matches = Object.entries(sug).filter(([, s]) => s && s.property_id);

      // Auto-apply high-confidence suggestions when threshold is set
      let autoApplied = 0;
      if (autolinkThreshold !== null && autolinkThreshold > 0) {
        const toApply = matches.filter(([, s]) => (s.confidence || 0) >= autolinkThreshold);
        for (const [txId, s] of toApply) {
          const tx = items.find((t) => t.id === txId);
          if (!tx) continue;
          try {
            await api.patch(`/transactions/${txId}`, { linked_property_id: s.property_id });
            autoApplied += 1;
            setItems((prev) => prev.filter((t) => t.id !== txId));
          } catch (err) {
            // Skip this tx and keep going with the rest of the batch. Log at
            // warn level so it's visible in dev but doesn't spam production.
            logger.warn("hv auto-link failed for", txId, err);
          }
        }
      }

      if (autoApplied > 0) {
        toast.success(`✨ ${autoApplied} vinculado${autoApplied === 1 ? "" : "s"} automáticamente (confianza ≥ ${Math.round(autolinkThreshold * 100)}%)`);
      } else if (matches.length > 0) {
        toast.success(`IA sugiere ${matches.length} vínculo${matches.length === 1 ? "" : "s"}`);
      } else {
        toast.info("La IA no encontró coincidencias claras");
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "La IA falló");
    } finally {
      setSuggesting(false);
    }
  };

  const visible = items.filter((t) => !hidden.has(t.id));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-xl bg-white max-h-[80vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl flex items-center gap-2">
            <AlertCircle className="w-5 h-5 text-[#C89A3F]" />
            Transferencias de alto valor
          </DialogTitle>
          <p className="text-xs text-[#8A9992]">
            Detectamos gastos ≥ {formatCurrency(threshold)} en los últimos 90 días sin
            vincular a un inmueble. Etiquétalos como reforma para incluirlos en el
            cost basis y cálculo de rentabilidad.
          </p>
          {items.length > 0 && properties.length > 0 && (
            <div className="mt-2 flex gap-2 flex-wrap">
              <button
                type="button"
                onClick={suggestAll}
                disabled={suggesting}
                data-testid="hv-suggest-btn"
                className="inline-flex items-center gap-1.5 h-8 px-3 rounded-lg text-xs font-semibold bg-[#0F291E] text-white hover:bg-[#1C7C54] disabled:opacity-60"
              >
                <Sparkles className="w-3.5 h-3.5" />
                {suggesting ? "Analizando…" : "Sugerir con IA"}
              </button>
              <button
                type="button"
                onClick={async () => {
                  const ids = visible.map((t) => t.id);
                  if (ids.length === 0) return;
                  const results = await Promise.allSettled(
                    ids.map((id) => api.patch(`/transactions/${id}`, { hv_reviewed: true }))
                  );
                  const okIds = ids.filter((_, i) => results[i].status === "fulfilled");
                  const failed = ids.length - okIds.length;
                  if (okIds.length > 0) {
                    setHidden((prev) => {
                      const next = new Set(prev);
                      okIds.forEach((id) => next.add(id));
                      return next;
                    });
                  }
                  if (failed === 0) {
                    toast.success(`Marcados ${okIds.length} como revisados`);
                  } else if (okIds.length > 0) {
                    toast.warning(`Marcados ${okIds.length}, ${failed} fallaron`);
                  } else {
                    toast.error("No se pudieron marcar");
                  }
                }}
                data-testid="hv-mark-all-btn"
                className="inline-flex items-center gap-1.5 h-8 px-3 rounded-lg text-xs font-semibold border border-[#EBEBEA] text-[#5F7368] hover:bg-[#FAFAF9]"
              >
                <X className="w-3.5 h-3.5" />
                Marcar todo como revisado
              </button>
            </div>
          )}
        </DialogHeader>

        <div className="flex-1 overflow-y-auto -mx-6 px-6" data-testid="hv-review-list">
          {loading ? (
            <div className="p-6 text-sm text-[#5F7368]">Cargando…</div>
          ) : visible.length === 0 ? (
            <div className="p-8 text-center text-sm text-[#8A9992]" data-testid="hv-review-empty">
              <Home className="w-8 h-8 mx-auto text-[#EBEBEA] mb-2" />
              <div>Todo revisado — sin gastos altos pendientes 🎉</div>
            </div>
          ) : (
            <ul className="divide-y divide-[#EBEBEA]">
              {visible.map((t) => {
                const sug = suggestions[t.id];
                const sugProp = sug?.property_id ? properties.find((p) => p.id === sug.property_id) : null;
                return (
                <li key={t.id} data-testid={`hv-item-${t.id}`} className="py-3 flex flex-col gap-2">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-semibold text-[#0A110E] truncate">
                        {t.description || t.category || "Movimiento"}
                      </div>
                      <div className="text-[11px] text-[#8A9992] mt-0.5 truncate">
                        {t.date}
                        {t.account_bank && ` · ${t.account_bank}`}
                        {t.account_alias && ` — ${t.account_alias}`}
                      </div>
                    </div>
                    <div className="text-sm font-bold tabular-nums text-[#D95D39] shrink-0">
                      {formatCurrency(t.amount, t.currency)}
                    </div>
                  </div>
                  {sugProp && (
                    <div
                      data-testid={`hv-suggestion-${t.id}`}
                      className="rounded-lg border border-[#1C7C54]/30 bg-[#1C7C54]/5 px-3 py-2 flex items-center gap-2"
                    >
                      <Sparkles className="w-3.5 h-3.5 text-[#1C7C54] shrink-0" />
                      <div className="flex-1 min-w-0 text-xs">
                        <div className="font-semibold text-[#1C7C54]">
                          IA sugiere: {sugProp.name}
                          <span className="text-[#8A9992] font-normal ml-1">
                            ({Math.round((sug.confidence || 0) * 100)}% confianza)
                          </span>
                        </div>
                        {sug.reason && (
                          <div className="text-[#5F7368] truncate">{sug.reason}</div>
                        )}
                      </div>
                      <button
                        type="button"
                        data-testid={`hv-accept-${t.id}`}
                        onClick={() => link(t, sug.property_id)}
                        disabled={savingId === t.id}
                        className="h-7 px-2 rounded-md text-xs font-semibold bg-[#1C7C54] text-white hover:bg-[#0F291E] disabled:opacity-60"
                      >
                        Aceptar
                      </button>
                    </div>
                  )}
                  <div className="flex items-center gap-2 flex-wrap">
                    {properties.length === 0 ? (
                      <span className="text-[11px] text-[#8A9992]">
                        Añade un inmueble para poder vincular
                      </span>
                    ) : (
                      <>
                        <select
                          data-testid={`hv-select-${t.id}`}
                          disabled={savingId === t.id}
                          onChange={(e) => e.target.value && link(t, e.target.value)}
                          defaultValue=""
                          className="h-8 px-2 rounded-md border border-[#EBEBEA] bg-white text-xs text-[#0A110E] focus:outline-none focus:border-[#1C7C54]/60 disabled:opacity-50 flex-1 min-w-[140px]"
                        >
                          <option value="">🏠 Vincular a inmueble…</option>
                          {properties.map((p) => (
                            <option key={p.id} value={p.id}>{p.name}</option>
                          ))}
                        </select>
                        <button
                          type="button"
                          onClick={() => skip(t)}
                          disabled={savingId === t.id}
                          data-testid={`hv-skip-${t.id}`}
                          className="h-8 px-3 rounded-md border border-[#EBEBEA] text-[11px] text-[#5F7368] hover:bg-[#EBEBEA] disabled:opacity-50 inline-flex items-center gap-1"
                        >
                          <X className="w-3 h-3" /> No es reforma
                        </button>
                      </>
                    )}
                  </div>
                </li>
                );
              })}
            </ul>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cerrar</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
